import { GroupParticipant, GroupChatMessage, GroupMeetingMessage, WebRTCConnectionState } from '@/types';
import { PUBLIC_STUN_SERVERS, enhanceSDPWithVideoBitrate } from './webrtcMeetingService';

export interface GroupMeetingCallbacks {
  onStatusChange?: (status: WebRTCConnectionState) => void;
  onParticipantsChange?: (participants: GroupParticipant[]) => void;
  onChatMessage?: (message: GroupChatMessage) => void;
  onActiveSpeakerChange?: (speakerId: string | null) => void;
  onScreenShareChange?: (isSharing: boolean, sharerId?: string) => void;
  onError?: (errorMessage: string) => void;
  onUserJoined?: (name: string) => void;
  onUserLeft?: (name: string) => void;
}

export class WebRTCGroupMeetingService {
  private peer: any = null;
  private localStream: MediaStream | null = null;
  private cameraVideoTrack: MediaStreamTrack | null = null;
  private screenStream: MediaStream | null = null;

  private isHost: boolean = false;
  private roomCode: string = '';
  private localParticipantId: string = '';
  private localParticipantName: string = '';

  private status: WebRTCConnectionState = 'idle';
  private callbacks: GroupMeetingCallbacks = {};

  // Connections map: peerId -> { call: MediaConnection, dataConn: DataConnection, stream: MediaStream }
  private peerConnections: Map<string, { call?: any; dataConn?: any; stream?: MediaStream }> = new Map();
  // Participants metadata map: id -> GroupParticipant
  private participantsMap: Map<string, GroupParticipant> = new Map();

  // Chat message history
  private chatMessages: GroupChatMessage[] = [];

  // Audio level analysis for active speaker detection
  private audioCtx: AudioContext | null = null;
  private localAnalyser: AnalyserNode | null = null;
  private remoteAnalysers: Map<string, AnalyserNode> = new Map();
  private audioPollInterval: any = null;
  private currentActiveSpeaker: string | null = null;

  // Recording
  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];
  private recordingCanvas: HTMLCanvasElement | null = null;
  private recordingCanvasCtx: CanvasRenderingContext2D | null = null;
  private recordingAnimFrame: number | null = null;
  private isRecording: boolean = false;
  private recordingStartTime: number = 0;
  private recordingAudioCtx: AudioContext | null = null;
  private offscreenVideoMap: Map<string, HTMLVideoElement> = new Map();

  public setCallbacks(callbacks: GroupMeetingCallbacks) {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  public getStatus(): WebRTCConnectionState {
    return this.status;
  }

  public getParticipants(): GroupParticipant[] {
    return Array.from(this.participantsMap.values());
  }

  public getLocalParticipantId(): string {
    return this.localParticipantId;
  }

  public getChatMessages(): GroupChatMessage[] {
    return [...this.chatMessages];
  }

  private setStatus(newStatus: WebRTCConnectionState) {
    this.status = newStatus;
    this.callbacks.onStatusChange?.(newStatus);
  }

  private emitParticipantsUpdate() {
    this.callbacks.onParticipantsChange?.(this.getParticipants());
  }

  public getRoomCode(): string {
    return this.roomCode;
  }

  /**
   * Generates a clean, friendly room code: e.g. "abc-def-ghi" or 6 digits
   */
  public static generateRoomCode(): string {
    const chars = 'abcdefghijkmnpqrstuvwxyz23456789';
    const pick = (len: number) => Array.from({ length: len }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
    return `${pick(3)}-${pick(4)}-${pick(3)}`;
  }

  /**
   * Host creates a new Group Meeting room
   */
  public async createRoom(roomCode: string, hostName: string, stream: MediaStream): Promise<string> {
    this.disconnect();
    this.isHost = true;
    this.roomCode = roomCode.trim().toLowerCase().replace(/[^a-z0-9-]/g, '');
    this.localParticipantName = hostName.trim() || 'Host';
    this.localStream = stream;
    this.cameraVideoTrack = stream.getVideoTracks()[0] || null;
    this.setStatus('initializing');

    const cleanCode = this.roomCode.replace(/-/g, '');
    // Host peer ID is deterministic on broker so guests can always find the room
    const hostPeerId = `meetsplit-grp-host-${cleanCode}`;
    this.localParticipantId = hostPeerId;

    try {
      const PeerModule = await import('peerjs');
      const PeerClass = (PeerModule.default || PeerModule.Peer || PeerModule) as any;

      this.peer = new PeerClass(hostPeerId, {
        config: {
          iceServers: PUBLIC_STUN_SERVERS
        },
        debug: 1
      });

      return new Promise((resolve, reject) => {
        this.peer.on('open', (id: string) => {
          console.log('[Group Host] Registered room on broker:', id);
          this.setStatus('connected');

          // Add self as local host participant
          this.participantsMap.set(this.localParticipantId, {
            id: this.localParticipantId,
            name: this.localParticipantName,
            isHost: true,
            isLocal: true,
            stream: this.localStream,
            audioMuted: !this.localStream?.getAudioTracks().some(t => t.enabled),
            videoOff: !this.localStream?.getVideoTracks().some(t => t.enabled),
            joinedAt: Date.now(),
            avatarBg: this.pickAvatarColor(this.localParticipantName)
          });

          this.setupAudioAnalysis();
          this.emitParticipantsUpdate();
          resolve(this.roomCode);
        });

        // Inbound DataConnection from incoming guest
        this.peer.on('connection', (conn: any) => {
          console.log('[Group Host] Guest data connection from:', conn.peer);
          this.setupDataConnection(conn);
        });

        // Inbound Call from incoming guest
        this.peer.on('call', (call: any) => {
          console.log('[Group Host] Inbound media call from:', call.peer);
          this.setupCall(call);
        });

        this.peer.on('error', (err: any) => {
          console.warn('[Group Host Error]', err);
          if (err.type === 'unavailable-id') {
            this.callbacks.onError?.('This meeting room code is already active. Please pick a new code.');
          } else {
            this.callbacks.onError?.(`Host connection error: ${err.message || 'STUN broker unreachable'}`);
          }
          this.setStatus('failed');
          reject(err);
        });

        this.peer.on('disconnected', () => {
          this.peer?.reconnect();
        });
      });
    } catch (err: any) {
      console.error('[Group Host Init Failed]', err);
      this.setStatus('failed');
      this.callbacks.onError?.(err.message || 'Failed to initialize group host');
      throw err;
    }
  }

  /**
   * Guest joins an existing Group Meeting room
   */
  public async joinRoom(roomCode: string, guestName: string, stream: MediaStream): Promise<void> {
    this.disconnect();
    this.isHost = false;
    this.roomCode = roomCode.trim().toLowerCase().replace(/[^a-z0-9-]/g, '');
    this.localParticipantName = guestName.trim() || 'Guest';
    this.localStream = stream;
    this.cameraVideoTrack = stream.getVideoTracks()[0] || null;
    this.setStatus('connecting');

    const cleanCode = this.roomCode.replace(/-/g, '');
    const randomGuestToken = Math.random().toString(36).substring(2, 8);
    const guestPeerId = `meetsplit-grp-${cleanCode}-${randomGuestToken}`;
    const hostPeerId = `meetsplit-grp-host-${cleanCode}`;
    this.localParticipantId = guestPeerId;

    try {
      const PeerModule = await import('peerjs');
      const PeerClass = (PeerModule.default || PeerModule.Peer || PeerModule) as any;

      this.peer = new PeerClass(guestPeerId, {
        config: {
          iceServers: PUBLIC_STUN_SERVERS
        },
        debug: 1
      });

      return new Promise((resolve, reject) => {
        let connectedToHost = false;

        this.peer.on('open', (id: string) => {
          console.log('[Group Guest] Registered as:', id);

          // Add self to local participants
          this.participantsMap.set(this.localParticipantId, {
            id: this.localParticipantId,
            name: this.localParticipantName,
            isHost: false,
            isLocal: true,
            stream: this.localStream,
            audioMuted: !this.localStream?.getAudioTracks().some(t => t.enabled),
            videoOff: !this.localStream?.getVideoTracks().some(t => t.enabled),
            joinedAt: Date.now(),
            avatarBg: this.pickAvatarColor(this.localParticipantName)
          });
          this.emitParticipantsUpdate();

          // Connect DataConnection to Host
          const hostDataConn = this.peer.connect(hostPeerId, { reliable: true });
          this.setupDataConnection(hostDataConn);

          hostDataConn.on('open', () => {
            console.log('[Group Guest] DataConnection opened to Host!');
            connectedToHost = true;
            this.setStatus('connected');

            // Send JOIN_REQUEST with name and metadata
            const joinMsg: GroupMeetingMessage = {
              type: 'JOIN_REQUEST',
              payload: {
                id: this.localParticipantId,
                name: this.localParticipantName,
                audioMuted: !this.localStream?.getAudioTracks().some(t => t.enabled),
                videoOff: !this.localStream?.getVideoTracks().some(t => t.enabled)
              }
            };
            hostDataConn.send(joinMsg);

            // Dial host with local media stream
            if (this.localStream) {
              const hostCall = this.peer.call(hostPeerId, this.localStream, {
                sdpTransform: (sdp: string) => enhanceSDPWithVideoBitrate(sdp, 3500)
              });
              this.setupCall(hostCall);
            }

            this.setupAudioAnalysis();
            resolve();
          });
        });

        // Listen for inbound calls from other mesh peers
        this.peer.on('call', (call: any) => {
          console.log('[Group Guest] Inbound call from peer:', call.peer);
          this.setupCall(call);
        });

        // Listen for inbound data connections from other mesh peers
        this.peer.on('connection', (conn: any) => {
          console.log('[Group Guest] Inbound data conn from peer:', conn.peer);
          this.setupDataConnection(conn);
        });

        this.peer.on('error', (err: any) => {
          console.warn('[Group Guest Error]', err);
          if (err.type === 'peer-unavailable') {
            this.callbacks.onError?.('Meeting room not found or host has not started the meeting yet.');
          } else {
            this.callbacks.onError?.(`Connection failed: ${err.message || 'Unable to join room'}`);
          }
          if (!connectedToHost) {
            this.setStatus('failed');
            reject(err);
          }
        });

        this.peer.on('disconnected', () => {
          this.peer?.reconnect();
        });
      });
    } catch (err: any) {
      console.error('[Group Guest Init Failed]', err);
      this.setStatus('failed');
      this.callbacks.onError?.(err.message || 'Failed to join group room');
      throw err;
    }
  }

  /**
   * Handles an incoming/outgoing DataConnection for messaging & signaling
   */
  private setupDataConnection(conn: any) {
    const peerId = conn.peer;

    const existing = this.peerConnections.get(peerId) || {};
    existing.dataConn = conn;
    this.peerConnections.set(peerId, existing);

    conn.on('data', (data: any) => {
      this.handleIncomingDataMessage(peerId, data);
    });

    conn.on('close', () => {
      console.log('[Group DataChannel] Closed for peer:', peerId);
      this.removeParticipant(peerId);
    });

    conn.on('error', (err: any) => {
      console.warn('[Group DataChannel Error]', peerId, err);
    });
  }

  /**
   * Handles an incoming/outgoing Media Call
   */
  private setupCall(call: any) {
    const peerId = call.peer;

    const existing = this.peerConnections.get(peerId) || {};
    existing.call = call;
    this.peerConnections.set(peerId, existing);

    // If we haven't answered yet, answer with our local stream
    if (!call.open && this.localStream) {
      call.answer(this.localStream, {
        sdpTransform: (sdp: string) => enhanceSDPWithVideoBitrate(sdp, 3500)
      });
    }

    call.on('stream', (remoteStream: MediaStream) => {
      console.log('[Group Call] Received remote stream from:', peerId, remoteStream.getTracks());
      const connInfo = this.peerConnections.get(peerId) || {};
      connInfo.stream = remoteStream;
      this.peerConnections.set(peerId, connInfo);

      // Attach stream to participant
      const participant = this.participantsMap.get(peerId);
      if (participant) {
        participant.stream = remoteStream;
        this.emitParticipantsUpdate();
      } else {
        // Create placeholder until metadata arrives
        this.participantsMap.set(peerId, {
          id: peerId,
          name: 'Participant',
          isHost: peerId.includes('-host-'),
          isLocal: false,
          stream: remoteStream,
          audioMuted: !remoteStream.getAudioTracks().some(t => t.enabled),
          videoOff: !remoteStream.getVideoTracks().some(t => t.enabled),
          joinedAt: Date.now(),
          avatarBg: this.pickAvatarColor(peerId)
        });
        this.emitParticipantsUpdate();
      }

      this.attachRemoteAudioAnalysis(peerId, remoteStream);
    });

    call.on('close', () => {
      console.log('[Group Call] Call closed for peer:', peerId);
      this.removeParticipant(peerId);
    });

    call.on('error', (err: any) => {
      console.warn('[Group Call Error]', peerId, err);
    });
  }

  /**
   * Processes incoming DataChannel message
   */
  private handleIncomingDataMessage(senderPeerId: string, msg: GroupMeetingMessage) {
    if (!msg || !msg.type) return;

    switch (msg.type) {
      case 'JOIN_REQUEST': {
        if (!this.isHost) return;
        const guestInfo = msg.payload;
        console.log('[Group Host] JOIN_REQUEST approved for:', guestInfo);

        // Add guest to host participants map
        const newGuest: GroupParticipant = {
          id: guestInfo.id,
          name: guestInfo.name || 'Guest',
          isHost: false,
          isLocal: false,
          audioMuted: !!guestInfo.audioMuted,
          videoOff: !!guestInfo.videoOff,
          joinedAt: Date.now(),
          avatarBg: this.pickAvatarColor(guestInfo.name || 'Guest')
        };
        this.participantsMap.set(guestInfo.id, newGuest);
        this.emitParticipantsUpdate();
        this.callbacks.onUserJoined?.(newGuest.name);

        // 1. Send ROOM_SNAPSHOT back to the new guest
        const snapshotPayload = Array.from(this.participantsMap.values()).map(p => ({
          id: p.id,
          name: p.name,
          isHost: p.isHost,
          audioMuted: p.audioMuted,
          videoOff: p.videoOff,
          joinedAt: p.joinedAt,
          avatarBg: p.avatarBg
        }));

        this.sendToPeer(senderPeerId, {
          type: 'ROOM_SNAPSHOT',
          payload: { participants: snapshotPayload }
        });

        // 2. Announce NEW_PEER_JOINED to all other existing peers so they dial newcomer
        this.broadcastDataMessage({
          type: 'NEW_PEER_JOINED',
          payload: {
            id: guestInfo.id,
            name: guestInfo.name,
            audioMuted: guestInfo.audioMuted,
            videoOff: guestInfo.videoOff,
            avatarBg: newGuest.avatarBg
          }
        }, senderPeerId); // Exclude the new guest itself
        break;
      }

      case 'ROOM_SNAPSHOT': {
        // Received by guest upon joining
        const list = msg.payload?.participants || [];
        list.forEach((p: any) => {
          if (p.id !== this.localParticipantId && !this.participantsMap.has(p.id)) {
            this.participantsMap.set(p.id, {
              id: p.id,
              name: p.name,
              isHost: p.isHost,
              isLocal: false,
              stream: this.peerConnections.get(p.id)?.stream || null,
              audioMuted: p.audioMuted,
              videoOff: p.videoOff,
              joinedAt: p.joinedAt,
              avatarBg: p.avatarBg || this.pickAvatarColor(p.name)
            });

            // If not connected to this peer yet, dial them
            if (!this.peerConnections.has(p.id) && this.localStream) {
              this.connectToMeshPeer(p.id);
            }
          }
        });
        this.emitParticipantsUpdate();
        break;
      }

      case 'NEW_PEER_JOINED': {
        // An existing peer learns about a new peer
        const p = msg.payload;
        if (p.id !== this.localParticipantId && !this.participantsMap.has(p.id)) {
          this.participantsMap.set(p.id, {
            id: p.id,
            name: p.name,
            isHost: false,
            isLocal: false,
            audioMuted: p.audioMuted,
            videoOff: p.videoOff,
            joinedAt: Date.now(),
            avatarBg: p.avatarBg || this.pickAvatarColor(p.name)
          });
          this.emitParticipantsUpdate();
          this.callbacks.onUserJoined?.(p.name);

          // Connect to the new peer in the mesh
          this.connectToMeshPeer(p.id);
        }
        break;
      }

      case 'PEER_MEDIA_STATE': {
        const { id, audioMuted, videoOff } = msg.payload || {};
        const participant = this.participantsMap.get(id || senderPeerId);
        if (participant) {
          if (audioMuted !== undefined) participant.audioMuted = audioMuted;
          if (videoOff !== undefined) participant.videoOff = videoOff;
          this.emitParticipantsUpdate();
        }
        break;
      }

      case 'PEER_RAISE_HAND': {
        const { id, handRaised } = msg.payload || {};
        const participant = this.participantsMap.get(id || senderPeerId);
        if (participant) {
          participant.handRaised = handRaised;
          this.emitParticipantsUpdate();
        }
        break;
      }

      case 'CHAT_MESSAGE': {
        const chatMsg: GroupChatMessage = {
          ...msg.payload,
          isLocal: false
        };
        this.chatMessages.push(chatMsg);
        this.callbacks.onChatMessage?.(chatMsg);
        break;
      }

      case 'SCREEN_SHARE_STATE': {
        const { id, isSharing } = msg.payload || {};
        const participant = this.participantsMap.get(id || senderPeerId);
        if (participant) {
          participant.isScreenSharing = isSharing;
          this.emitParticipantsUpdate();
        }
        this.callbacks.onScreenShareChange?.(isSharing, id || senderPeerId);
        break;
      }

      case 'PEER_LEFT': {
        const { id, name } = msg.payload || {};
        this.removeParticipant(id || senderPeerId, name);
        break;
      }

      case 'HOST_MUTED_ALL': {
        if (!this.isHost) {
          this.muteAudio(true);
        }
        break;
      }
    }
  }

  /**
   * Establishes mesh P2P call & DataConnection between this peer and another room participant
   */
  private connectToMeshPeer(targetPeerId: string) {
    if (!this.peer || this.peer.destroyed || targetPeerId === this.localParticipantId) return;

    console.log('[Group Mesh] Establishing connection to peer:', targetPeerId);
    // 1. DataConnection
    const dataConn = this.peer.connect(targetPeerId, { reliable: true });
    this.setupDataConnection(dataConn);

    // 2. Media Call
    if (this.localStream) {
      const call = this.peer.call(targetPeerId, this.localStream, {
        sdpTransform: (sdp: string) => enhanceSDPWithVideoBitrate(sdp, 3500)
      });
      this.setupCall(call);
    }
  }

  private sendToPeer(peerId: string, msg: GroupMeetingMessage) {
    const connInfo = this.peerConnections.get(peerId);
    if (connInfo?.dataConn && connInfo.dataConn.open) {
      try {
        connInfo.dataConn.send(msg);
      } catch (err) {
        console.warn('[SendToPeer Error]', peerId, err);
      }
    }
  }

  private broadcastDataMessage(msg: GroupMeetingMessage, excludePeerId?: string) {
    this.peerConnections.forEach((connInfo, peerId) => {
      if (peerId !== excludePeerId && connInfo.dataConn && connInfo.dataConn.open) {
        try {
          connInfo.dataConn.send(msg);
        } catch (err) {
          console.warn('[Broadcast Error]', peerId, err);
        }
      }
    });
  }

  /**
   * Send a chat message to everyone in the room
   */
  public sendChatMessage(text: string): GroupChatMessage | null {
    if (!text.trim()) return null;

    const chatMsg: GroupChatMessage = {
      id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      senderId: this.localParticipantId,
      senderName: this.localParticipantName,
      text: text.trim(),
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      isLocal: true
    };

    this.chatMessages.push(chatMsg);
    this.callbacks.onChatMessage?.(chatMsg);

    this.broadcastDataMessage({
      type: 'CHAT_MESSAGE',
      payload: chatMsg
    });

    return chatMsg;
  }

  /**
   * Toggle or set microphone state
   */
  public muteAudio(muted: boolean) {
    if (this.localStream) {
      this.localStream.getAudioTracks().forEach(track => {
        track.enabled = !muted;
      });
    }

    const self = this.participantsMap.get(this.localParticipantId);
    if (self) {
      self.audioMuted = muted;
      this.emitParticipantsUpdate();
    }

    this.broadcastDataMessage({
      type: 'PEER_MEDIA_STATE',
      payload: { id: this.localParticipantId, audioMuted: muted }
    });
  }

  /**
   * Toggle or set camera state
   */
  public muteVideo(videoOff: boolean) {
    if (this.localStream) {
      this.localStream.getVideoTracks().forEach(track => {
        track.enabled = !videoOff;
      });
    }

    const self = this.participantsMap.get(this.localParticipantId);
    if (self) {
      self.videoOff = videoOff;
      this.emitParticipantsUpdate();
    }

    this.broadcastDataMessage({
      type: 'PEER_MEDIA_STATE',
      payload: { id: this.localParticipantId, videoOff }
    });
  }

  /**
   * Updates local stream tracks during live meeting (e.g. switching camera, mic, or flipping mobile camera)
   */
  public updateLocalStream(newStream: MediaStream) {
    this.localStream = newStream;
    const newVideoTrack = newStream.getVideoTracks()[0];
    const newAudioTrack = newStream.getAudioTracks()[0];

    this.peerConnections.forEach((connInfo) => {
      const pc: RTCPeerConnection = connInfo.call?.peerConnection;
      if (pc) {
        if (newVideoTrack) {
          const videoSender = pc.getSenders().find(s => s.track && s.track.kind === 'video');
          if (videoSender) {
            videoSender.replaceTrack(newVideoTrack).catch(err => {
              console.warn('[replaceTrack video error]', err);
            });
          }
        }
        if (newAudioTrack) {
          const audioSender = pc.getSenders().find(s => s.track && s.track.kind === 'audio');
          if (audioSender) {
            audioSender.replaceTrack(newAudioTrack).catch(err => {
              console.warn('[replaceTrack audio error]', err);
            });
          }
        }
      }
    });

    const self = this.participantsMap.get(this.localParticipantId);
    if (self) {
      self.stream = newStream;
      this.emitParticipantsUpdate();
    }
  }

  /**
   * Toggle Hand Raise
   */
  public toggleRaiseHand(): boolean {
    const self = this.participantsMap.get(this.localParticipantId);
    if (!self) return false;

    const nextState = !self.handRaised;
    self.handRaised = nextState;
    this.emitParticipantsUpdate();

    this.broadcastDataMessage({
      type: 'PEER_RAISE_HAND',
      payload: { id: this.localParticipantId, handRaised: nextState }
    });

    return nextState;
  }

  /**
   * Start Screen Sharing
   */
  public async startScreenShare(): Promise<MediaStream> {
    try {
      let displayStream: MediaStream;
      try {
        displayStream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            cursor: 'always',
            frameRate: { ideal: 30, max: 60 }
          } as any,
          audio: true
        });
      } catch (err: any) {
        // Fallback: request without audio constraint if audio failed or unsupported
        if (err.name !== 'NotAllowedError' && err.name !== 'AbortError') {
          displayStream = await navigator.mediaDevices.getDisplayMedia({
            video: {
              cursor: 'always',
              frameRate: { ideal: 30, max: 60 }
            } as any
          });
        } else {
          throw err;
        }
      }

      this.screenStream = displayStream;
      const screenVideoTrack = displayStream.getVideoTracks()[0];

      if (!screenVideoTrack) {
        throw new Error('No video track found in screen share stream');
      }

      // 1. Remember and detach the camera track from localStream
      if (this.localStream) {
        const currentCamTrack = this.localStream.getVideoTracks()[0];
        if (currentCamTrack && currentCamTrack !== screenVideoTrack) {
          this.cameraVideoTrack = currentCamTrack;
          this.localStream.removeTrack(currentCamTrack);
        }
        this.localStream.addTrack(screenVideoTrack);
      }

      // 2. Replace video track in all active peer connections
      this.peerConnections.forEach((connInfo) => {
        const pc: RTCPeerConnection = connInfo.call?.peerConnection;
        if (pc) {
          let videoSender = pc.getSenders().find(s => s.track && s.track.kind === 'video');
          if (!videoSender && pc.getTransceivers) {
            const t = pc.getTransceivers().find(
              tr => tr.sender?.track?.kind === 'video' || tr.receiver?.track?.kind === 'video'
            );
            if (t) videoSender = t.sender;
          }
          if (videoSender) {
            videoSender.replaceTrack(screenVideoTrack).catch(err => {
              console.warn('[replaceTrack Error]', err);
            });
          } else {
            try {
              const streamToSend = this.localStream || this.screenStream;
              if (streamToSend) {
                pc.addTrack(screenVideoTrack, streamToSend);
              }
            } catch (e) {
              console.warn('[addTrack fallback error]', e);
            }
          }
        }
      });

      // 3. Update local participant stream and screen sharing state
      const self = this.participantsMap.get(this.localParticipantId);
      if (self) {
        self.isScreenSharing = true;
        self.videoOff = false; // Screen sharing provides live video
        self.stream = this.screenStream; // Immediately show screen share on local user's own tile
        this.emitParticipantsUpdate();
      }

      // 4. Broadcast state to all peers
      this.broadcastDataMessage({
        type: 'SCREEN_SHARE_STATE',
        payload: { id: this.localParticipantId, isSharing: true }
      });
      this.callbacks.onScreenShareChange?.(true, this.localParticipantId);

      // 5. Handle user clicking native browser "Stop sharing" bar
      screenVideoTrack.onended = () => {
        this.stopScreenShare();
      };

      return this.screenStream;
    } catch (err: any) {
      console.warn('[Screen Share Cancelled/Failed]', err);
      throw err;
    }
  }

  /**
   * Stop Screen Sharing
   */
  public stopScreenShare() {
    if (this.screenStream) {
      this.screenStream.getTracks().forEach(t => t.stop());
      this.screenStream = null;
    }

    // 1. Revert track in localStream back to cameraVideoTrack
    if (this.localStream) {
      const screenTracks = this.localStream.getVideoTracks();
      screenTracks.forEach(t => this.localStream?.removeTrack(t));

      if (this.cameraVideoTrack && this.cameraVideoTrack.readyState === 'live') {
        this.localStream.addTrack(this.cameraVideoTrack);
      }
    }

    // 2. Revert sender track in all active RTCPeerConnections
    this.peerConnections.forEach((connInfo) => {
      const pc: RTCPeerConnection = connInfo.call?.peerConnection;
      if (pc) {
        let videoSender = pc.getSenders().find(s => s.track && s.track.kind === 'video');
        if (!videoSender && pc.getTransceivers) {
          const t = pc.getTransceivers().find(
            tr => tr.sender?.track?.kind === 'video' || tr.receiver?.track?.kind === 'video'
          );
          if (t) videoSender = t.sender;
        }
        if (videoSender) {
          const trackToRestore = (this.cameraVideoTrack && this.cameraVideoTrack.readyState === 'live')
            ? this.cameraVideoTrack
            : null;
          videoSender.replaceTrack(trackToRestore).catch(console.warn);
        }
      }
    });

    // 3. Update local participant stream and screen sharing state
    const self = this.participantsMap.get(this.localParticipantId);
    if (self) {
      self.isScreenSharing = false;
      self.stream = this.localStream;
      this.emitParticipantsUpdate();
    }

    // 4. Broadcast state to all peers
    this.broadcastDataMessage({
      type: 'SCREEN_SHARE_STATE',
      payload: { id: this.localParticipantId, isSharing: false }
    });
    this.callbacks.onScreenShareChange?.(false, this.localParticipantId);
  }

  /**
   * Host action: Mute all other participants
   */
  public hostMuteAll() {
    if (!this.isHost) return;
    this.broadcastDataMessage({
      type: 'HOST_MUTED_ALL',
      payload: {}
    });
  }

  /**
   * Audio Level Analysis for Active Speaker Detection
   */
  private setupAudioAnalysis() {
    this.stopAudioAnalysis();
    try {
      const AudioCtxClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtxClass) return;

      this.audioCtx = new AudioCtxClass();

      if (this.localStream && this.localStream.getAudioTracks().length > 0) {
        const localSource = this.audioCtx.createMediaStreamSource(this.localStream);
        this.localAnalyser = this.audioCtx.createAnalyser();
        this.localAnalyser.fftSize = 64;
        localSource.connect(this.localAnalyser);
      }

      this.audioPollInterval = setInterval(() => {
        this.pollActiveSpeaker();
      }, 180);
    } catch (err) {
      console.warn('[AudioAnalysis Init Warning]', err);
    }
  }

  private attachRemoteAudioAnalysis(peerId: string, stream: MediaStream) {
    if (!this.audioCtx || stream.getAudioTracks().length === 0) return;
    try {
      const source = this.audioCtx.createMediaStreamSource(stream);
      const analyser = this.audioCtx.createAnalyser();
      analyser.fftSize = 64;
      source.connect(analyser);
      this.remoteAnalysers.set(peerId, analyser);
    } catch (err) {
      console.warn('[Attach Remote Audio Warning]', err);
    }
  }

  private pollActiveSpeaker() {
    let maxVolume = 18; // threshold
    let loudSpeakerId: string | null = null;

    // Check local
    if (this.localAnalyser && this.localStream?.getAudioTracks().some(t => t.enabled)) {
      const buffer = new Uint8Array(this.localAnalyser.frequencyBinCount);
      this.localAnalyser.getByteFrequencyData(buffer);
      const avg = buffer.reduce((acc, val) => acc + val, 0) / buffer.length;
      if (avg > maxVolume) {
        maxVolume = avg;
        loudSpeakerId = this.localParticipantId;
      }
    }

    // Check remotes
    this.remoteAnalysers.forEach((analyser, peerId) => {
      const participant = this.participantsMap.get(peerId);
      if (participant && !participant.audioMuted) {
        const buffer = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(buffer);
        const avg = buffer.reduce((acc, val) => acc + val, 0) / buffer.length;
        if (avg > maxVolume) {
          maxVolume = avg;
          loudSpeakerId = peerId;
        }
      }
    });

    if (loudSpeakerId !== this.currentActiveSpeaker) {
      this.currentActiveSpeaker = loudSpeakerId;

      // Update participant isSpeaking tags
      this.participantsMap.forEach((p, id) => {
        p.isSpeaking = id === loudSpeakerId;
      });
      this.emitParticipantsUpdate();
      this.callbacks.onActiveSpeakerChange?.(loudSpeakerId);
    }
  }

  private stopAudioAnalysis() {
    if (this.audioPollInterval) {
      clearInterval(this.audioPollInterval);
      this.audioPollInterval = null;
    }
    this.remoteAnalysers.clear();
    this.localAnalyser = null;
    if (this.audioCtx) {
      this.audioCtx.close().catch(() => {});
      this.audioCtx = null;
    }
  }

  /**
   * Safe rounded rectangle path for canvas rendering (cross-browser compatible)
   */
  private roundRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
    const radius = Math.min(r, w / 2, h / 2);
    if (typeof (ctx as any).roundRect === 'function') {
      ctx.beginPath();
      (ctx as any).roundRect(x, y, w, h, radius);
      return;
    }
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + w - radius, y);
    ctx.arcTo(x + w, y, x + w, y + radius, radius);
    ctx.lineTo(x + w, y + h - radius);
    ctx.arcTo(x + w, y + h, x + w - radius, y + h, radius);
    ctx.lineTo(x + radius, y + h);
    ctx.arcTo(x, y + h, x, y + h - radius, radius);
    ctx.lineTo(x, y + radius);
    ctx.arcTo(x, y, x + radius, y, radius);
    ctx.closePath();
  }

  /**
   * Draws video in cover mode into cell
   */
  private drawCover(
    ctx: CanvasRenderingContext2D,
    video: HTMLVideoElement,
    x: number,
    y: number,
    w: number,
    h: number
  ) {
    const vw = video.videoWidth || 1;
    const vh = video.videoHeight || 1;
    const targetAspect = w / h;
    const videoAspect = vw / vh;

    let sx = 0;
    let sy = 0;
    let sw = vw;
    let sh = vh;

    if (videoAspect > targetAspect) {
      sw = vh * targetAspect;
      sx = (vw - sw) / 2;
    } else {
      sh = vw / targetAspect;
      sy = (vh - sh) / 2;
    }

    try {
      ctx.drawImage(video, sx, sy, sw, sh, x, y, w, h);
    } catch {}
  }

  /**
   * Finds the best active video element for a participant (DOM query first, offscreen video fallback)
   */
  private getVideoElementForParticipant(p: GroupParticipant): HTMLVideoElement | null {
    try {
      const domVideo = document.querySelector<HTMLVideoElement>(`video[data-participant-id="${p.id}"]`);
      if (domVideo && domVideo.readyState >= 2 && domVideo.videoWidth > 0) {
        return domVideo;
      }
    } catch {}

    if (p.isLocal) {
      const stream = p.isScreenSharing ? (this.screenStream || p.stream) : (p.stream || this.localStream);
      if (!stream || !stream.getVideoTracks().some(t => t.enabled && t.readyState === 'live')) {
        return null;
      }
      return this.getOrCreateOffscreenVideo(p.id, stream);
    }

    const remoteStream = p.stream || this.peerConnections.get(p.id)?.stream;
    if (!remoteStream || !remoteStream.getVideoTracks().some(t => t.enabled && t.readyState === 'live')) {
      return null;
    }
    return this.getOrCreateOffscreenVideo(p.id, remoteStream);
  }

  /**
   * Retrieves screen share video element for full-frame presentation recording
   */
  private getScreenShareVideo(screenSharer: GroupParticipant): HTMLVideoElement | null {
    try {
      const domScreen = document.querySelector<HTMLVideoElement>('video[data-is-sharing="true"]');
      if (domScreen && domScreen.readyState >= 2 && domScreen.videoWidth > 0) {
        return domScreen;
      }

      const sharerDom = document.querySelector<HTMLVideoElement>(`video[data-participant-id="${screenSharer.id}"]`);
      if (sharerDom && sharerDom.readyState >= 2 && sharerDom.videoWidth > 0) {
        return sharerDom;
      }
    } catch {}

    const stream = screenSharer.isLocal
      ? (this.screenStream || screenSharer.stream)
      : (screenSharer.stream || this.peerConnections.get(screenSharer.id)?.stream);

    if (!stream) return null;
    return this.getOrCreateOffscreenVideo(`screen-${screenSharer.id}`, stream);
  }

  private getOrCreateOffscreenVideo(key: string, stream: MediaStream): HTMLVideoElement | null {
    let offVideo = this.offscreenVideoMap.get(key);
    if (!offVideo) {
      offVideo = document.createElement('video');
      offVideo.muted = true;
      offVideo.playsInline = true;
      offVideo.autoplay = true;
      this.offscreenVideoMap.set(key, offVideo);
    }

    if (offVideo.srcObject !== stream) {
      offVideo.srcObject = stream;
      offVideo.play().catch(() => {});
    }

    return offVideo.readyState >= 2 && offVideo.videoWidth > 0 ? offVideo : null;
  }

  private cleanupOffscreenVideos() {
    this.offscreenVideoMap.forEach(v => {
      try {
        v.pause();
        v.srcObject = null;
        v.remove();
      } catch {}
    });
    this.offscreenVideoMap.clear();
  }

  private drawParticipantAvatar(
    ctx: CanvasRenderingContext2D,
    p: GroupParticipant,
    x: number,
    y: number,
    w: number,
    h: number,
    isSpeaking: boolean
  ) {
    const grad = ctx.createLinearGradient(x, y, x + w, y + h);
    grad.addColorStop(0, '#0b1329');
    grad.addColorStop(1, '#1e1b4b');
    ctx.fillStyle = grad;
    ctx.fillRect(x, y, w, h);

    const cx = x + w / 2;
    const cy = y + h / 2 - 14;
    const radius = Math.min(w, h) * 0.18;

    if (isSpeaking) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, radius + 14, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(16, 185, 129, 0.28)';
      ctx.fill();
      ctx.restore();
    }

    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    const avatarGrad = ctx.createLinearGradient(cx - radius, cy - radius, cx + radius, cy + radius);
    avatarGrad.addColorStop(0, '#9333ea');
    avatarGrad.addColorStop(1, '#4f46e5');
    ctx.fillStyle = avatarGrad;
    ctx.fill();
    ctx.lineWidth = 3;
    ctx.strokeStyle = isSpeaking ? '#10b981' : 'rgba(255, 255, 255, 0.25)';
    ctx.stroke();

    const initials = (p.name || 'User')
      .split(' ')
      .filter(Boolean)
      .map(n => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || 'U';

    ctx.fillStyle = '#ffffff';
    ctx.font = `bold ${Math.max(16, Math.round(radius * 0.75))}px Inter, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(initials, cx, cy);
    ctx.restore();

    ctx.save();
    ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
    ctx.font = '600 14px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(p.name, cx, cy + radius + 26);
    ctx.restore();
  }

  /**
   * Composite Meeting Video Recorder
   * Automatically adapts between Full-Screen Screen Share and Dynamic Responsive Grid with Active Speaker Glow
   */
  public async startCompositeRecording(): Promise<void> {
    if (this.isRecording) return;
    this.isRecording = true;
    this.recordedChunks = [];
    this.recordingStartTime = Date.now();

    // Create offscreen canvas for rendering multi-grid (1080p Full HD)
    this.recordingCanvas = document.createElement('canvas');
    this.recordingCanvas.width = 1920;
    this.recordingCanvas.height = 1080;
    this.recordingCanvasCtx = this.recordingCanvas.getContext('2d');

    const canvasStream = this.recordingCanvas.captureStream(30);

    // Mix audio
    this.recordingAudioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const dest = this.recordingAudioCtx.createMediaStreamDestination();

    if (this.localStream && this.localStream.getAudioTracks().length > 0) {
      try {
        const src = this.recordingAudioCtx.createMediaStreamSource(this.localStream);
        src.connect(dest);
      } catch {}
    }

    this.peerConnections.forEach((conn) => {
      if (conn.stream && conn.stream.getAudioTracks().length > 0) {
        try {
          const src = this.recordingAudioCtx!.createMediaStreamSource(conn.stream);
          src.connect(dest);
        } catch {}
      }
    });

    dest.stream.getAudioTracks().forEach(t => canvasStream.addTrack(t));

    // Canvas render loop
    const renderMeetingToCanvas = () => {
      if (!this.isRecording || !this.recordingCanvasCtx || !this.recordingCanvas) return;
      const ctx = this.recordingCanvasCtx;
      const w = this.recordingCanvas.width;
      const h = this.recordingCanvas.height;

      // Dark background
      ctx.fillStyle = '#080c16';
      ctx.fillRect(0, 0, w, h);

      const allParticipants = this.getParticipants();
      // Check if ANY participant is actively sharing their screen
      const screenSharer = allParticipants.find(p => p.isScreenSharing);

      const elapsedSec = Math.floor((Date.now() - this.recordingStartTime) / 1000);
      const mins = Math.floor(elapsedSec / 60).toString().padStart(2, '0');
      const secs = (elapsedSec % 60).toString().padStart(2, '0');
      const timeStr = `${mins}:${secs}`;

      if (screenSharer) {
        // ========================================================
        // MODE 1: SCREEN SHARE ONLY FULLSCREEN (USER REQUIREMENT)
        // Saved recording displays ONLY the Screen Share in 1080p full frame
        // ========================================================
        const screenVideo = this.getScreenShareVideo(screenSharer);

        if (screenVideo && screenVideo.readyState >= 2 && screenVideo.videoWidth > 0) {
          const vw = screenVideo.videoWidth;
          const vh = screenVideo.videoHeight;
          const videoAspect = vw / vh;
          const canvasAspect = w / h;

          let drawW = w;
          let drawH = h;
          let drawX = 0;
          let drawY = 0;

          if (videoAspect > canvasAspect) {
            drawW = w;
            drawH = w / videoAspect;
            drawY = (h - drawH) / 2;
          } else {
            drawH = h;
            drawW = h * videoAspect;
            drawX = (w - drawW) / 2;
          }

          try {
            ctx.drawImage(screenVideo, drawX, drawY, drawW, drawH);
          } catch {}
        } else {
          ctx.save();
          ctx.fillStyle = '#0f172a';
          ctx.fillRect(80, 80, w - 160, h - 160);
          ctx.strokeStyle = 'rgba(59, 130, 246, 0.4)';
          ctx.lineWidth = 2;
          this.roundRectPath(ctx, 80, 80, w - 160, h - 160, 24);
          ctx.stroke();

          ctx.fillStyle = '#60a5fa';
          ctx.font = 'bold 24px Inter, sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText(`Presenting Screen: ${screenSharer.name}`, w / 2, h / 2 - 10);
          ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
          ctx.font = '16px Inter, sans-serif';
          ctx.fillText('Capturing presentation feed in Full HD...', w / 2, h / 2 + 25);
          ctx.restore();
        }

        // Top-left presentation badge pill
        ctx.save();
        ctx.fillStyle = 'rgba(30, 58, 138, 0.85)';
        this.roundRectPath(ctx, 24, 24, 340, 42, 21);
        ctx.fill();
        ctx.strokeStyle = 'rgba(96, 165, 250, 0.5)';
        ctx.lineWidth = 1.5;
        this.roundRectPath(ctx, 24, 24, 340, 42, 21);
        ctx.stroke();

        ctx.fillStyle = '#38bdf8';
        ctx.font = 'bold 15px Inter, sans-serif';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(`⚡ ${screenSharer.isLocal ? 'You are' : screenSharer.name + ' is'} presenting screen`, 44, 45);
        ctx.restore();

      } else {
        // ========================================================
        // MODE 2: CAMERA MEETING GRID VIEW (USER REQUIREMENT)
        // All participants displayed in clean responsive grid.
        // Active Speaker gets a prominent glowing Emerald Green (#10b981) border!
        // ========================================================
        const participants = allParticipants.length > 0 ? allParticipants : [
          {
            id: this.localParticipantId,
            name: this.localParticipantName || 'Host',
            isHost: this.isHost,
            isLocal: true,
            stream: this.localStream,
            audioMuted: false,
            videoOff: false,
            joinedAt: Date.now()
          }
        ];

        const count = participants.length;
        let cols = 1;
        let rows = 1;
        if (count === 1) { cols = 1; rows = 1; }
        else if (count === 2) { cols = 2; rows = 1; }
        else if (count <= 4) { cols = 2; rows = 2; }
        else if (count <= 6) { cols = 3; rows = 2; }
        else if (count <= 9) { cols = 3; rows = 3; }
        else { cols = 4; rows = Math.ceil(count / 4); }

        const padX = 24;
        const padY = 80;
        const bottomPad = 24;
        const gap = 16;
        const availW = w - (padX * 2) - ((cols - 1) * gap);
        const availH = h - padY - bottomPad - ((rows - 1) * gap);
        const cellW = availW / cols;
        const cellH = availH / rows;
        const radius = 18;

        participants.forEach((p, idx) => {
          const col = idx % cols;
          const row = Math.floor(idx / cols);
          const x = padX + col * (cellW + gap);
          const y = padY + row * (cellH + gap);
          const isSpeaking = Boolean(p.isSpeaking || (this.currentActiveSpeaker && p.id === this.currentActiveSpeaker));

          // 1. Clip and draw cell contents
          ctx.save();
          this.roundRectPath(ctx, x, y, cellW, cellH, radius);
          ctx.clip();

          ctx.fillStyle = '#0f172a';
          ctx.fillRect(x, y, cellW, cellH);

          const video = this.getVideoElementForParticipant(p);
          const hasVideo = !p.videoOff && video && video.readyState >= 2 && video.videoWidth > 0;

          if (hasVideo && video) {
            if (p.isLocal) {
              ctx.save();
              ctx.translate(x + cellW, y);
              ctx.scale(-1, 1);
              this.drawCover(ctx, video, 0, 0, cellW, cellH);
              ctx.restore();
            } else {
              this.drawCover(ctx, video, x, y, cellW, cellH);
            }
          } else {
            this.drawParticipantAvatar(ctx, p, x, y, cellW, cellH, isSpeaking);
          }
          ctx.restore();

          // 2. Active Speaker Glowing Border or Standard Sleek Border
          ctx.save();
          if (isSpeaking) {
            // PROMINENT EMERALD GLOW BORDER FOR ACTIVE SPEAKER
            ctx.shadowColor = '#10b981';
            ctx.shadowBlur = 28;
            ctx.strokeStyle = '#10b981';
            ctx.lineWidth = 6;
            this.roundRectPath(ctx, x, y, cellW, cellH, radius);
            ctx.stroke();
          } else {
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)';
            ctx.lineWidth = 2;
            this.roundRectPath(ctx, x, y, cellW, cellH, radius);
            ctx.stroke();
          }
          ctx.restore();

          // 3. Bottom-left Participant Name Pill
          ctx.save();
          const badgeText = `${p.name}${p.isLocal ? ' (You)' : ''}${p.isHost ? ' • Host' : ''}`;
          ctx.font = '600 13px Inter, sans-serif';
          const textMetrics = ctx.measureText(badgeText);
          const pillW = textMetrics.width + 38;
          const pillH = 30;
          const pillX = x + 12;
          const pillY = y + cellH - pillH - 12;

          ctx.fillStyle = 'rgba(15, 23, 42, 0.85)';
          this.roundRectPath(ctx, pillX, pillY, pillW, pillH, 15);
          ctx.fill();
          ctx.strokeStyle = isSpeaking ? 'rgba(16, 185, 129, 0.5)' : 'rgba(255, 255, 255, 0.15)';
          ctx.lineWidth = 1;
          this.roundRectPath(ctx, pillX, pillY, pillW, pillH, 15);
          ctx.stroke();

          // Mic status dot: Emerald if speaking, Red if muted, White if unmuted
          ctx.beginPath();
          ctx.arc(pillX + 14, pillY + pillH / 2, 4.5, 0, Math.PI * 2);
          ctx.fillStyle = isSpeaking ? '#10b981' : p.audioMuted ? '#ef4444' : '#e2e8f0';
          ctx.fill();

          ctx.fillStyle = '#ffffff';
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          ctx.fillText(badgeText, pillX + 26, pillY + pillH / 2);
          ctx.restore();

          // 4. Hand Raised Badge if active
          if (p.handRaised) {
            ctx.save();
            ctx.fillStyle = 'rgba(245, 158, 11, 0.9)';
            this.roundRectPath(ctx, x + 12, y + 12, 110, 28, 14);
            ctx.fill();
            ctx.fillStyle = '#ffffff';
            ctx.font = 'bold 12px Inter, sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('✋ Hand Raised', x + 67, y + 26);
            ctx.restore();
          }
        });
      }

      // Top-Left Watermark: MeetSplit AI • Room Code
      ctx.save();
      ctx.fillStyle = 'rgba(15, 23, 42, 0.85)';
      this.roundRectPath(ctx, 24, 20, 280, 42, 21);
      ctx.fill();
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
      ctx.lineWidth = 1;
      this.roundRectPath(ctx, 24, 20, 280, 42, 21);
      ctx.stroke();

      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 15px Inter, sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(`MeetSplit AI • ${this.roomCode}`, 42, 41);
      ctx.restore();

      // Top-Right REC Badge with live duration
      ctx.save();
      const recW = 120;
      const recX = w - recW - 24;
      ctx.fillStyle = 'rgba(220, 38, 38, 0.2)';
      this.roundRectPath(ctx, recX, 20, recW, 42, 21);
      ctx.fill();
      ctx.strokeStyle = 'rgba(239, 68, 68, 0.5)';
      ctx.lineWidth = 1;
      this.roundRectPath(ctx, recX, 20, recW, 42, 21);
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(recX + 18, 41, 5, 0, Math.PI * 2);
      ctx.fillStyle = '#ef4444';
      ctx.fill();

      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 14px "Courier New", monospace';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(`REC ${timeStr}`, recX + 30, 41);
      ctx.restore();

      this.recordingAnimFrame = requestAnimationFrame(renderMeetingToCanvas);
    };

    renderMeetingToCanvas();

    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
      ? 'video/webm;codecs=vp9,opus'
      : MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
      ? 'video/webm;codecs=vp8,opus'
      : 'video/webm';

    this.mediaRecorder = new MediaRecorder(canvasStream, {
      mimeType,
      videoBitsPerSecond: 4000000
    });

    this.mediaRecorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        this.recordedChunks.push(e.data);
      }
    };

    this.mediaRecorder.start(1000);
  }

  public async stopCompositeRecording(): Promise<{ blob: Blob; durationSec: number }> {
    return new Promise((resolve) => {
      if (!this.mediaRecorder || !this.isRecording) {
        resolve({ blob: new Blob(), durationSec: 0 });
        return;
      }

      this.isRecording = false;
      const durationSec = Math.round((Date.now() - this.recordingStartTime) / 1000);

      if (this.recordingAnimFrame) {
        cancelAnimationFrame(this.recordingAnimFrame);
        this.recordingAnimFrame = null;
      }

      if (this.recordingAudioCtx) {
        this.recordingAudioCtx.close().catch(() => {});
        this.recordingAudioCtx = null;
      }

      this.cleanupOffscreenVideos();

      this.mediaRecorder.onstop = () => {
        const finalBlob = new Blob(this.recordedChunks, { type: 'video/webm' });
        this.recordedChunks = [];
        resolve({ blob: finalBlob, durationSec });
      };

      this.mediaRecorder.stop();
    });
  }

  private removeParticipant(peerId: string, name?: string) {
    const participant = this.participantsMap.get(peerId);
    const displayName = name || participant?.name || 'Participant';

    const offVideo = this.offscreenVideoMap.get(peerId);
    if (offVideo) {
      try {
        offVideo.pause();
        offVideo.srcObject = null;
        offVideo.remove();
      } catch {}
      this.offscreenVideoMap.delete(peerId);
    }

    this.participantsMap.delete(peerId);
    this.peerConnections.delete(peerId);
    this.remoteAnalysers.delete(peerId);

    this.emitParticipantsUpdate();
    this.callbacks.onUserLeft?.(displayName);
  }

  private pickAvatarColor(name: string): string {
    const colors = [
      'from-purple-600 to-indigo-600',
      'from-blue-600 to-cyan-600',
      'from-emerald-600 to-teal-600',
      'from-rose-600 to-pink-600',
      'from-amber-600 to-orange-600',
      'from-violet-600 to-fuchsia-600'
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  }

  /**
   * Leave room & cleanup everything
   */
  public disconnect() {
    if (this.status === 'idle') return;

    if (this.isRecording) {
      this.stopCompositeRecording().catch(() => {});
    }

    this.cleanupOffscreenVideos();
    if (this.recordingAudioCtx) {
      this.recordingAudioCtx.close().catch(() => {});
      this.recordingAudioCtx = null;
    }

    if (this.screenStream) {
      this.stopScreenShare();
    }

    // Stop and release local camera and mic tracks
    if (this.localStream) {
      this.localStream.getTracks().forEach(t => {
        try { t.stop(); } catch {}
      });
      this.localStream = null;
    }
    if (this.cameraVideoTrack) {
      try { this.cameraVideoTrack.stop(); } catch {}
      this.cameraVideoTrack = null;
    }

    // Broadcast departure
    this.broadcastDataMessage({
      type: 'PEER_LEFT',
      payload: { id: this.localParticipantId, name: this.localParticipantName }
    });

    this.stopAudioAnalysis();

    // Close all peer calls and data connections
    this.peerConnections.forEach((conn) => {
      try { conn.call?.close(); } catch {}
      try { conn.dataConn?.close(); } catch {}
    });
    this.peerConnections.clear();
    this.participantsMap.clear();
    this.chatMessages = [];

    // Destroy PeerJS
    if (this.peer) {
      try {
        this.peer.destroy();
      } catch {}
      this.peer = null;
    }

    this.setStatus('idle');
  }

}

export const webrtcGroupMeetingService = new WebRTCGroupMeetingService();
